<script setup lang="ts">
// Celoobrazovková výzva "Aktualizuj aplikaci" (CO-PODCHYTIT, část 4): server vrátil 426 app_outdated.
// Web a PWA: Obnovit = nechat service worker stáhnout novou verzi a načíst stránku znovu.
// Nativní aplikace: odkaz do obchodu (VITE_STORE_ANDROID_URL, VITE_STORE_IOS_URL; prázdné = tlačítko skryté).
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { Button, Card } from '@wac-buddy/ui'
import BrandLogo from '@/components/BrandLogo.vue'
import { isNative, nativePlatform } from '@/lib/native'
import { useUiStore } from '@/stores/ui'

const { t } = useI18n()
const ui = useUiStore()

const native = isNative()
const refreshing = ref(false)

const text = computed(() =>
  ui.outdated?.minVersion
    ? t('outdated.text', { min: ui.outdated.minVersion, current: __APP_VERSION__ })
    : t('outdated.textNoMin'),
)

/** Odkaz do obchodu pro tuto platformu; prázdný = neznámý, tlačítko se nezobrazí. */
const store = computed(() => {
  const platform = nativePlatform()
  const url =
    platform === 'android'
      ? import.meta.env.VITE_STORE_ANDROID_URL
      : platform === 'ios'
        ? import.meta.env.VITE_STORE_IOS_URL
        : ''
  const trimmed = (url ?? '').trim()
  return trimmed && platform !== 'web'
    ? { url: trimmed, label: `outdated.store.${platform}` }
    : null
})

async function refresh(): Promise<void> {
  refreshing.value = true
  try {
    // Nový service worker se aktivuje sám (autoUpdate); update() ho jen donutí zkontrolovat server.
    const registration = await navigator.serviceWorker?.getRegistration()
    await registration?.update()
  } catch {
    /* bez service workeru stačí samotné načtení */
  }
  location.reload()
}
</script>

<template>
  <main
    class="flex min-h-dvh flex-col items-center justify-center bg-bg px-4 py-8 text-text"
    data-testid="outdated"
  >
    <BrandLogo variant="horizontal" :height="40" :alt="t('app.name')" class="mb-6" />
    <Card class="flex w-full max-w-sm flex-col gap-4">
      <h1 class="m-0 text-xl font-semibold">{{ t('outdated.title') }}</h1>
      <p class="m-0 text-text-2" data-testid="outdated-text">{{ text }}</p>

      <template v-if="native">
        <Button
          v-if="store"
          as="a"
          variant="primary"
          block
          :href="store.url"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="outdated-store"
        >
          {{ t(store.label) }}
        </Button>
        <p v-else class="m-0 text-sm text-text-muted" data-testid="outdated-native-hint">
          {{ t('outdated.nativeHint') }}
        </p>
      </template>

      <template v-else>
        <Button
          variant="primary"
          block
          :loading="refreshing"
          data-testid="outdated-refresh"
          @click="refresh"
        >
          {{ refreshing ? t('outdated.refreshing') : t('outdated.refresh') }}
        </Button>
        <p class="m-0 text-sm text-text-muted">{{ t('outdated.refreshHint') }}</p>
      </template>
    </Card>
  </main>
</template>
